import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { ViaticosRequestForm } from "./ViaticosRequestForm";
import { ViaticosDetail } from "./ViaticosDetail";
import { ViaticosVehicles } from "./ViaticosVehicles";
import { ViaticosVisitTypes } from "./ViaticosVisitTypes";
import { ViaticosAdminDashboard } from "./ViaticosAdminDashboard";
import { Plus, CaretLeft, MagnifyingGlass, Car, MapPin, CalendarBlank, User, Tag, ChartPie, Trash, CheckSquare, Square } from "@phosphor-icons/react";

const STATUS_COLORS = {
  solicitado: "bg-blue-100 text-blue-800",
  aprobado: "bg-emerald-100 text-emerald-800",
  rechazado: "bg-red-100 text-red-800",
  en_viaje: "bg-amber-100 text-amber-800",
  en_liquidacion: "bg-purple-100 text-purple-800",
  liquidado: "bg-teal-100 text-teal-800",
  cerrado: "bg-slate-200 text-slate-700",
};

const STATUS_LABELS = {
  solicitado: "Solicitado",
  aprobado: "Aprobado",
  rechazado: "Rechazado",
  en_viaje: "En Viaje",
  en_liquidacion: "En Liquidacion",
  liquidado: "Liquidado",
  cerrado: "Cerrado",
};

const VISIT_LABELS = {
  fidelizacion: "Fidelizacion",
  demostracion: "Demostracion",
  prospeccion: "Prospeccion",
};

export default function ViaticosModule() {
  const { user, token } = useAuth();
  const [view, setView] = useState("list");
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin" || user?.role === "auxiliar";
  const isAdminOrAux = isAdmin;

  const loadSolicitudes = useCallback(async () => {
    try {
      let url = `${API}/viaticos/solicitudes`;
      const params = [];
      if (filterStatus) params.push(`status=${filterStatus}`);
      if (params.length) url += "?" + params.join("&");
      const { data } = await axios.get(url, { headers });
      setSolicitudes(data);
    } catch { toast.error("Error cargando solicitudes"); }
    setLoading(false);
  }, [token, filterStatus]);

  useEffect(() => { loadSolicitudes(); }, [loadSolicitudes]);

  const filtered = solicitudes.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.user_name?.toLowerCase().includes(q) ||
      s.location?.toLowerCase().includes(q) ||
      s.clients?.some(c => c.toLowerCase?.().includes(q)) ||
      s.visit_type?.toLowerCase().includes(q)
    );
  });

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selected.size} solicitud(es) de viáticos? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/viaticos/solicitudes/bulk-delete`, { ids: [...selected] }, { headers });
      toast.success(`${selected.size} solicitud(es) eliminada(s)`);
      setSelected(new Set());
      loadSolicitudes();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
    finally { setDeleting(false); }
  };

  if (view === "new") {
    return (
      <div>
        <button onClick={() => setView("list")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="viaticos-back-btn">
          <CaretLeft size={16} /> Volver a solicitudes
        </button>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Nueva Solicitud de Viaticos</h1>
        <ViaticosRequestForm onSuccess={() => { setView("list"); loadSolicitudes(); }} />
      </div>
    );
  }

  if (view === "detail" && selectedId) {
    return (
      <ViaticosDetail
        solicitudId={selectedId}
        onBack={() => { setSelectedId(null); setView("list"); loadSolicitudes(); }}
      />
    );
  }

  if (view === "vehicles") {
    return (
      <div>
        <button onClick={() => setView("list")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
          <CaretLeft size={16} /> Volver a solicitudes
        </button>
        <ViaticosVehicles />
      </div>
    );
  }

  if (view === "visit-types") {
    return (
      <div>
        <button onClick={() => setView("list")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
          <CaretLeft size={16} /> Volver a solicitudes
        </button>
        <ViaticosVisitTypes />
      </div>
    );
  }

  if (view === "dashboard") {
    return <ViaticosAdminDashboard onBack={() => setView("list")} />;
  }

  return (
    <div className="space-y-4" data-testid="viaticos-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Viaticos</h1>
        <div className="flex items-center gap-2">
          {isAdminOrAux && (
            <button onClick={() => setView("dashboard")} className="px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 flex items-center gap-1.5" data-testid="viaticos-dashboard-btn">
              <ChartPie size={16} weight="duotone" /> Dashboard
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setView("visit-types")} className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1.5" data-testid="viaticos-visit-types-btn">
              <Tag size={16} weight="duotone" /> Tipos de Visita
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setView("vehicles")} className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1.5" data-testid="viaticos-vehicles-btn">
              <Car size={16} weight="duotone" /> Vehiculos
            </button>
          )}
          <button onClick={() => setView("new")} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="viaticos-new-btn">
            <Plus size={16} weight="bold" /> Nueva Solicitud
          </button>
        </div>
      </div>

      {/* Filters + Bulk Actions */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="viaticos-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="viaticos-filter-status">
          <option value="">Todos los estatus</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {isAdmin && filtered.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={toggleAll} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 flex items-center gap-1.5" data-testid="viaticos-select-all">
              {selected.size === filtered.length ? <CheckSquare size={14} weight="fill" className="text-blue-600" /> : <Square size={14} />}
              {selected.size > 0 ? `${selected.size} seleccionado(s)` : "Seleccionar todos"}
            </button>
            {selected.size > 0 && (
              <button onClick={handleBulkDelete} disabled={deleting} className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50" data-testid="viaticos-bulk-delete">
                <Trash size={14} /> {deleting ? "Eliminando..." : `Eliminar (${selected.size})`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <MapPin size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No hay solicitudes de viaticos</p>
          <p className="text-sm text-slate-400 mt-1">Haz clic en "Nueva Solicitud" para crear una</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => (
            <div key={s.id} className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow cursor-pointer ${selected.has(s.id) ? "ring-2 ring-blue-400 border-blue-300" : ""}`} data-testid={`viatico-card-${s.id}`}>
              <div className="flex items-start gap-3">
                {isAdmin && (
                  <button onClick={(e) => toggleSelect(s.id, e)} className="mt-1 shrink-0" data-testid={`viatico-check-${s.id}`}>
                    {selected.has(s.id) ? <CheckSquare size={20} weight="fill" className="text-blue-600" /> : <Square size={20} className="text-slate-300 hover:text-slate-500" />}
                  </button>
                )}
                <div className="flex-1 min-w-0" onClick={() => { setSelectedId(s.id); setView("detail"); }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_COLORS[s.status] || "bg-slate-100 text-slate-600"}`}>
                          {STATUS_LABELS[s.status] || s.status}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {VISIT_LABELS[s.visit_type] || s.visit_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-sm">
                        {isAdmin && (
                          <span className="flex items-center gap-1 text-slate-600"><User size={14} /> {s.user_name}</span>
                        )}
                        <span className="flex items-center gap-1 text-slate-600"><MapPin size={14} /> {s.location || "Sin ubicacion"}</span>
                        <span className="flex items-center gap-1 text-slate-500"><CalendarBlank size={14} /> {s.date_start} → {s.date_end}</span>
                      </div>
                      {s.clients?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {s.clients.map((c, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{typeof c === 'object' ? c.name : c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-slate-800">${(s.amount_requested || 0).toLocaleString()}</p>
                      <p className="text-[11px] text-slate-400">Solicitado</p>
                      {s.approved_amount != null && (
                        <>
                          <p className="text-sm font-semibold text-emerald-600 mt-1">${s.approved_amount.toLocaleString()}</p>
                          <p className="text-[11px] text-slate-400">Aprobado</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t text-xs text-slate-400">
                    <span>{s.vehicle_plate ? `Vehiculo: ${s.vehicle_plate}` : "Sin vehiculo"}</span>
                    <span>{s.created_at?.slice(0, 10)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
